/**
 * Compact inline validation warnings component.
 *
 * Renders type compatibility and join path warnings as small chips/alerts
 * below the inline editor or inspector fields. Includes optional "Fix"
 * buttons that auto-fill transformation expressions.
 *
 * Used in both MappingTableRow (inline editor) and InspectorTabs (LogicEditorTab).
 */

import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BuildIcon from '@mui/icons-material/Build';
import type { TypeCompatibilityIssue, SuggestedFix } from './typeCompatibility';

// ── Join warning type (from Phase B) ────────────────────────────────

export interface JoinPathWarning {
  message: string;
  suggestion?: string;
}

// ── Props ───────────────────────────────────────────────────────────

export interface InlineValidationWarningsProps {
  /** Type compatibility issues from evaluateFieldTypeCompatibility() */
  typeIssues?: TypeCompatibilityIssue[];
  /** Join path warning from validateJoinPath() */
  joinWarning?: JoinPathWarning | null;
  /** Callback when user clicks a "Fix" button on a type issue */
  onApplyFix?: (fix: SuggestedFix) => void;
  /** Compact mode: show fewer details (for table row) */
  compact?: boolean;
  /** Maximum issues to display (default: 3) */
  maxDisplay?: number;
}

// ── Severity styling ────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, string> = {
  error: '#ef4444',
  warning: '#f59e0b',
  join: '#8b5cf6',
};

// ── Component ───────────────────────────────────────────────────────

const InlineValidationWarnings: React.FC<InlineValidationWarningsProps> = ({
  typeIssues = [],
  joinWarning,
  onApplyFix,
  compact = false,
  maxDisplay = 3,
}) => {
  const hasIssues = typeIssues.length > 0 || !!joinWarning;
  if (!hasIssues) return null;

  const displayedIssues = typeIssues.slice(0, maxDisplay);
  const overflowCount = typeIssues.length - maxDisplay;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        mt: compact ? 0.5 : 1,
        mb: compact ? 0 : 0.5,
      }}
    >
      {/* Type compatibility warnings */}
      {displayedIssues.map((issue, idx) => {
        const color = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.warning;
        const Icon = issue.severity === 'error' ? ErrorOutlineIcon : WarningAmberIcon;

        return (
          <Box
            key={`type-${idx}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: compact ? 0.5 : 1,
              py: compact ? 0.25 : 0.5,
              borderRadius: 1,
              backgroundColor: alpha(color, 0.06),
              border: `1px solid ${alpha(color, 0.15)}`,
            }}
          >
            <Icon sx={{ fontSize: compact ? 12 : 14, color, flexShrink: 0 }} />
            <Tooltip title={issue.remediation} arrow placement="top">
              <Typography
                variant="caption"
                sx={{
                  fontSize: compact ? 10 : 11,
                  color,
                  fontWeight: 500,
                  lineHeight: 1.3,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: compact ? 'nowrap' : 'normal',
                }}
              >
                {issue.message}
              </Typography>
            </Tooltip>
            {issue.suggestedFix && onApplyFix && (
              <Chip
                icon={<BuildIcon sx={{ fontSize: 10 }} />}
                label={issue.suggestedFix.label}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onApplyFix(issue.suggestedFix!);
                }}
                sx={{
                  height: compact ? 16 : 18,
                  fontSize: compact ? 9 : 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: alpha(color, 0.1),
                  color,
                  border: `1px solid ${alpha(color, 0.2)}`,
                  '& .MuiChip-icon': { color, fontSize: 10 },
                  '&:hover': {
                    backgroundColor: alpha(color, 0.18),
                  },
                }}
              />
            )}
          </Box>
        );
      })}

      {/* Overflow indicator */}
      {overflowCount > 0 && (
        <Typography
          variant="caption"
          sx={{ fontSize: 10, color: 'text.secondary', pl: 1 }}
        >
          +{overflowCount} more warning{overflowCount > 1 ? 's' : ''}
        </Typography>
      )}

      {/* Join path warning */}
      {joinWarning && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: compact ? 0.5 : 1,
            py: compact ? 0.25 : 0.5,
            borderRadius: 1,
            backgroundColor: alpha(SEVERITY_COLORS.join, 0.06),
            border: `1px solid ${alpha(SEVERITY_COLORS.join, 0.15)}`,
          }}
        >
          <LinkOffIcon
            sx={{ fontSize: compact ? 12 : 14, color: SEVERITY_COLORS.join, flexShrink: 0 }}
          />
          <Tooltip title={joinWarning.suggestion || ''} arrow placement="top">
            <Typography
              variant="caption"
              sx={{
                fontSize: compact ? 10 : 11,
                color: SEVERITY_COLORS.join,
                fontWeight: 500,
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: compact ? 'nowrap' : 'normal',
              }}
            >
              {joinWarning.message}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(InlineValidationWarnings);
